import { get } from "lodash";
import { CategoryField } from "../../typings/categories";
import { Enums } from "../../thor.enums";
import { isAdvertiser } from "../../utils/organization";

interface OnlinePriceOptions {
    currency?: Enums.Currency;
    format?: boolean;
    start_at?: string;
}

export const MEMBERSHIP_ONLINE_PUBLICATION_FEE_PER_LOT = 0.2;
export const ADVERTISER_ONLINE_SALE_PUBLICATION_FEE = 350;

class OnlinePriceService {
    public static $inject: string[] = [
        "$translate",
        "authService",
        "configService",
        "localeNumber",
        "sitesService"
    ];

    private $t;

    constructor(
        $translate: ng.translate.ITranslateService,
        private authService,
        private configService,
        private localeNumber,
        private sitesService
    ) {
        this.$t = $translate.instant;
    }

    public calculateFinalPrice(
        price: number = 0,
        rates: any,
        categoryField: string,
        { currency = Enums.Currency.EUR, format = true, start_at }: OnlinePriceOptions
    ): string | number {
        if (!price) {
            return;
        }
        const { onlineCommissionRate, voluntaryRate } = this.setCommissionRates(rates);
        const vehiclesFeesWithTaxes: number = this.localeNumber.getVehiclesFees({
            currency,
            format: false,
            start_at,
            with_taxes: true
        });

        if (categoryField && categoryField === CategoryField.VEH) {
            price = price + vehiclesFeesWithTaxes + (price / 100) * voluntaryRate;
        } else {
            price = price + (price / 100) * onlineCommissionRate + (price / 100) * voluntaryRate;
        }

        price = this.localeNumber.roundNbr(price);

        return format ? this.localeNumber.formatCurrency(price, currency) : price;
    }

    public calculateCommissionRatePrice(price: number, voluntaryRate: number) {
        if (!price) {
            return;
        }
        return this.localeNumber.roundNbr((price / 100) * voluntaryRate);
    }

    public calculateOnlineCommissionRatePrice(
        price: number,
        categoryField: CategoryField,
        { currency = Enums.Currency.EUR, start_at }: OnlinePriceOptions
    ) {
        if (!price) {
            return;
        }
        const { onlineCommissionRate } = this.configService;
        return categoryField === CategoryField.VEH
            ? this.localeNumber.getVehiclesFees({ currency, start_at, with_taxes: true })
            : this.localeNumber.roundNbr((price / 100) * onlineCommissionRate);
    }

    public getPublicationFeeAmount(count: number = 1) {
        return this.localeNumber.formatCurrency(
            isAdvertiser(this.authService.organization)
                ? ADVERTISER_ONLINE_SALE_PUBLICATION_FEE
                : MEMBERSHIP_ONLINE_PUBLICATION_FEE_PER_LOT * count
        );
    }

    public getCommissionRatesLabel(
        price: number,
        rates: any,
        categoryField: CategoryField,
        { currency, start_at }: OnlinePriceOptions
    ) {
        if (!price) {
            return;
        }
        const { onlineCommissionRate, voluntaryRate } = this.setCommissionRates(rates);
        const commissionValues = {
            BUYER_FEES: this.localeNumber.formatCurrency((price / 100) * voluntaryRate, currency),
            SITE: this.sitesService.siteName
        };
        return categoryField === CategoryField.VEH
            ? this.$t("frais_acheteurs_commissionnement_site_veh", {
                  ...commissionValues,
                  SITE_FEES: this.localeNumber.getVehiclesFees({ currency, start_at, with_taxes: true })
              })
            : this.$t("frais_acheteurs_commissionnement_site", {
                  ...commissionValues,
                  SITE_FEES: this.localeNumber.formatCurrency((price / 100) * onlineCommissionRate, currency),
                  SITE_FEES_PERCENT: this.localeNumber.formatPercent(onlineCommissionRate)
              });
    }

    public setCommissionRates(rates: any) {
        return {
            onlineCommissionRate: this.configService.onlineCommissionRate,
            voluntaryRate: typeof rates.voluntaryRate === "number" ?
                rates.voluntaryRate :
                get(this.authService.organization, "options.commission_rate.voluntary")
        };
    }
}

angular
    .module("thor.core.online-price.service", [])
    .service("onlinePriceService", OnlinePriceService);
